import {
  TDashboardComponent,
  TDashboardRenderComponentProps,
} from "MyDashboards/components/ComponentRegistry/types";
import useDashboardEditor from "MyDashboards/state/editor";
import { Element } from "MyDashboards/state/editorTypes";
import px from "MyDashboards/utils/px";
import HighChart from "MyDashboards/views/ChartsTesting/HighChart";
import { CellValue } from "MyDashboards/views/ChartsTesting/types";
import usePeriods from "backend/hooks/model/usePeriods";
import { Options, XAxisOptions } from "highcharts";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import Query from "qpr-base/querybuilder/Query";
import { mea_t } from "qpr-base/tplutils/utils";
import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import ChartEditor from "./ChartEditor";
import { getChartStyles, seriesColors } from "./ChartStyles";
import { TChartComponentData } from "./types";

type TElementAndSeries = {
  elementId: string;
  elementName: string;
  seriesName: string;
};
const Render = (props: TDashboardRenderComponentProps) => {
  const { el, onReady } = props;
  const ctx = useAppContext();
  const compData = React.useMemo(() => {
    return el.component?.data as TChartComponentData;
  }, [el]);
  const selectedSeries = compData?.selectedSeries?.value;
  const [isElemLoading, setIsElemLoading] = React.useState(false);
  const { isLoading: isPeriodsLoading, data: allPeriods } = usePeriods();
  const [elemError, setElemError] = React.useState();
  const [elementsAndSeries, setElementsAndSeries] = React.useState<
    TElementAndSeries[]
  >([]);
  const editorMode = useDashboardEditor((e) => e.mode); // is affected by edit/preivew toggle switch
  const [visiblePeriods, setVisiblePeriods] = React.useState<string[]>([]);
  const [valuesTable, setValuesTable] = React.useState<
    {
      id: string;
      name: string;
      values: CellValue[];
    }[]
  >([]);
  const { t } = useTranslation();

  // Queries for the chart elements
  const {
    data: loadedElementList,
    isLoading,
    isError: listError,
  } = useQuery(
    `${compData.selectedElement?.value}-${compData.selectedSeries?.value}`,
    () => {
      return Query(compData.selectedElement?.value)
        .childobjects({
          criteria: {
            typename:
              selectedSeries === "kpi_act" //_and_benchmarks_act"
                ? "Benchmark"
                : "Sub-KPI",
          },
        })
        .attributes(
          "name",
          "periodlevel",
          'value(series="JS_ATTR", period="latestforseries")'
        )
        .fetchValues<{ id: string; periodlevel: string; JS_ATTR: string }>();
    },
    {
      staleTime: Infinity,
      enabled:
        !!compData.selectedElement?.value &&
        /* selectedSeries === "kpi_act_and_benchmarks_act") ||
        /*selectedSeries === "kpi_act_and_subkpis_act" ||*/
        selectedSeries === "subkpis_act",
      retry: 3,
    }
  );

  /**
   * first Figure out all the elements related to this setting
   */
  React.useEffect(() => {
    if (
      compData.selectedSeries?.value === "kpi_act" &&
      compData.selectedElement
    ) {
      setElementsAndSeries([
        {
          elementId: compData.selectedElement.value,
          seriesName: "ACT",
          elementName: compData.selectedElement.label,
        },
      ]);
    }

    if (
      compData.selectedSeries?.value === "kpi_act_tar" &&
      compData.selectedElement
    ) {
      setElementsAndSeries([
        {
          elementId: compData.selectedElement.value,
          seriesName: "ACT",
          elementName: "ACT",
        },
        {
          elementId: compData.selectedElement.value,
          seriesName: "TAR",
          elementName: compData.selectedElement.label,
        },
      ]);
    }
    /*if (
      loadedElementList &&
      compData.selectedSeries?.value === "kpi_act_and_subkpis_act" &&
      compData.selectedElement
    ) {
      setElementsAndSeries([
        {
          elementId: compData.selectedElement?.value || "",
          seriesName: "ACT",
          elementName: compData.selectedElement?.label || "",
        },
        ...loadedElementList.map((res) => ({
          elementId: res.id,
          seriesName: "ACT",
          elementName: mea_t("Name", res.JS_ATTR, ctx.language),
        })),
      ]);
    }*/
    if (
      loadedElementList &&
      compData.selectedSeries?.value === "subkpis_act" &&
      compData.selectedElement
    ) {
      setElementsAndSeries(
        loadedElementList.map((res) => ({
          elementId: res.id,
          periodlevel: res.periodlevel,
          seriesName: "ACT",
          elementName: mea_t("Name", res.JS_ATTR, ctx.language),
        }))
      );
    }
    /*if (
      loadedElementList &&
      compData.selectedSeries?.value === "kpi_act_and_benchmarks_act" &&
      compData.selectedElement
    ) {
      setElementsAndSeries([
        {
          elementId: compData.selectedElement?.value || "",
          seriesName: "ACT",
          elementName: compData.selectedElement?.label || "",
        },
        ...loadedElementList.map((res) => ({
          elementId: res.id,
          seriesName: "BENCHMARK",
          elementName: mea_t("Name", res.JS_ATTR, ctx.language),
        })),
      ]);
    }*/
  }, [compData, loadedElementList, compData.selectedSeries]);

  React.useEffect(() => {
    if (
      allPeriods &&
      !isPeriodsLoading &&
      compData.selectedPeriods &&
      compData.selectedPeriods.length > 0
    ) {
      let periods: string[] = [];

      if (compData.selectedPeriods.length === 1) {
        periods.push(compData.selectedPeriods[0]);
      } else {
        const firstPeriodIndex = allPeriods.findIndex(
          (p) => p.name === compData.selectedPeriods?.[0]
        );
        const lastPeriodIndex = allPeriods.findIndex(
          (p) => p.name === compData.selectedPeriods?.[1]
        );
        const periodlevel = allPeriods[firstPeriodIndex]?.periodlevel;
        if (periodlevel) {
          for (let i = firstPeriodIndex; i <= lastPeriodIndex; i += 1) {
            if (allPeriods[i].periodlevel === periodlevel)
              periods.push(allPeriods[i].name);
          }
        }
      }
      setVisiblePeriods(periods);
    }
  }, [elementsAndSeries, allPeriods, compData.selectedPeriods]);

  /*
    Modify chart options based on series type
  */

  const modifyOptions = (options: any): Options => {
    let modifiedOptions: any = options;
    const { categories } = options.xAxis as XAxisOptions;
    const seriesLength = options.series?.length;

    switch (selectedSeries) {
      /*case "kpi_act_and_benchmarks_act":
        if (!seriesLength) {
          return {
            ...options,
            xAxis: {
              ...options.xAxis,
              categories: categories?.map((cat, idx) =>
                idx === 0 ? "" : cat
              ),
            },
            series: {
              ...options.series,
              data:
                options.chart.type === "pie"
                  ? options.series.data.map((ser: any, idx: number) =>
                      idx === 0 ? { ...ser, name: t("") } : ser
                    )
                  : options.series.data,
            },
          };
        }
        return {
          ...options,
          series: options?.series?.map((serie: any, index: number) => ({
            ...serie,
            name: index === 0 ? t("") : serie.name,
          })),
        };*/
      case "kpi_act_tar":
        if (compData.selectedChart === "line" && seriesLength) {
          return {
            ...options,
            series: options?.series?.map((ser: any, index: number) => ({
              ...ser,
              type: index === 0 ? "spline" : "areaspline",
              shadow: index !== 0 ? false : ser.shadow,
            })),
          };
        }
        break;
      default:
        return modifiedOptions;
    }
    return modifiedOptions;
  };

  /**
   * Fetch values from the server
   */
  React.useEffect(() => {
    if (elementsAndSeries && visiblePeriods) {
      const promises: Array<Promise<CellValue>> = [];
      setIsElemLoading(true);
      elementsAndSeries.forEach((elSer) => {
        visiblePeriods.forEach((period) => {
          const q = Query(elSer.elementId)
            .attributes(
              "name",
              ["value", { period: period, series: elSer.seriesName }],
              ["unit", { period: period, series: elSer.seriesName }],
              [
                "valuewithoutsymbol",
                { period: period, series: elSer.seriesName },
              ]
            )
            .fetchValues<CellValue>()
            .then((value) => value[0]);
          promises.push(q);
        });
      });

      Promise.all(promises)
        .then((values) => {
          const tableRows: {
            id: string;
            name: string;
            values: CellValue[];
          }[] = [];
          const list = [...values];
          elementsAndSeries.forEach((kpi) => {
            const values = list.splice(0, visiblePeriods.length);
            tableRows.push({
              id: kpi.elementId,
              name:
                compData.selectedSeries?.value === "subkpis_act" /*||
                compData.selectedSeries?.value === "kpi_act_and_subkpis_act" ||
                compData.selectedSeries?.value === "kpi_act_and_benchmarks_act"*/
                  ? kpi.elementName
                  : kpi.seriesName,
              values,
            });
          });
          setIsElemLoading(false);
          setValuesTable(tableRows);
        })
        .catch((err) => {
          setElemError(err);
        });
    }
  }, [elementsAndSeries, visiblePeriods]);

  /**
   * Create Chart options
   */
  const chartOptions = React.useMemo(() => {
    return {
      type: compData.selectedChart || "column",
      title: compData.selectedElement?.label || "",
      categories: visiblePeriods,
      series: valuesTable,
      chartDef: compData.selectedSeries?.value,
      rtl: ctx.rtl,
      styles: getChartStyles(selectedSeries),
      width: el?.component?.width,
      height: (el?.component?.width || 0) * (el.height / el.width),
      disableAnimation: true,
    };
  }, [
    compData,
    visiblePeriods,
    valuesTable,
    ctx.rtl,
    el.component?.width,
    el.component?.height,
    el.width,
    el.height,
    editorMode,
  ]);

  const refOnReadyIsCalled = React.useRef(false);
  React.useEffect(() => {
    // || isElemLoading
    const isReady = !isLoading;
    const isError = listError || elemError;
    const iv = setTimeout(() => {
      if (!refOnReadyIsCalled.current) {
        if (isError && onReady) {
          refOnReadyIsCalled.current = true;
          onReady(el, isError);
        }
        if (el && onReady && isReady && !isElemLoading) {
          refOnReadyIsCalled.current = true;
          onReady(el, isError);
        }
      }
    }, 200);
    return () => {
      window.clearTimeout(iv);
    };
  }, [chartOptions, isLoading, isElemLoading]);

  if (!el.component) return null;
  if (isLoading || isPeriodsLoading || valuesTable.length === 0)
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      />
    );
  /**
   * Render the chart
   */
  return (
    <div
      key={`chart_${el.id}`}
      style={{
        width: px(el.component.width),
        height: px(el.component.height),
        transform: `scale(${el.width / el.component.width})`,
        transformOrigin: ctx.rtl ? "top right" : "top left",
        justifyContent: "unset",
        alignItems: "unset",
      }}
    >
      <HighChart
        key={chartOptions.type}
        modifyOptions={modifyOptions}
        options={chartOptions as any}
        colors={seriesColors}
      />
    </div>
  );
};

const Chart: TDashboardComponent = {
  editorMode: "chart",
  Edit: ({
    el,
    onChange,
  }: {
    el: Element;
    onChange: (el: Partial<Element>) => void;
  }) => {
    return <ChartEditor el={el} onChange={onChange} />;
  },
  Render: ({
    el,
    onReady,
  }: {
    el: Element;
    onReady?: (el: Element, isError?: boolean) => void;
  }) => {
    const ctx = useAppContext();
    if (!el.component) {
      return null;
    }
    return <Render el={el} onReady={onReady} />;
  },
};
export default Chart;
